'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * The state a folded band runs on: whether it is open, and what happens when a
 * link elsewhere on the page points at it.
 *
 * A hook rather than a component so the band stays open anatomy — a tenant
 * composes its own title, body and empty state into the parts rather than
 * handing a shared component a renderer. It is also what keeps those parts
 * server components: the only client code a band needs is this, in the tenant's
 * own client composition.
 *
 * It decides nothing about copy or appearance, and nothing about which bands a
 * page has.
 *
 * Not `@vs-shop/headless`'s `useScrollToItemOnHash`, which answers a different
 * question: that one scrolls to an element it finds by attribute, once, on
 * mount. A folded band has to be OPENED before scrolling to it is worth
 * anything, it has to answer a second link to the same band — which fires no
 * navigation, only a hash change — and what it scrolls to is the trigger it
 * already holds a ref to rather than an element it goes looking for.
 */

export type UseProductBandDisclosureParameters = {
  /**
   * The fragment this band answers to, without its `#` — what a page puts on
   * the band's own element and what its anchor strip links to.
   *
   * A folded band is closed, so a shopper following such a link would otherwise
   * land on a collapsed title with the thing they asked for still hidden.
   */
  readonly anchor: string;
  /**
   * Whether the band starts open. Required rather than defaulted: on a page
   * running several bands, which one opens for free is a real editorial
   * decision, and one made by omission would be invisible at the call site.
   */
  readonly defaultOpen: boolean;
};

export type ProductBandDisclosureState = {
  readonly isOpen: boolean;
  /** Pass to `ProductBandDisclosure`'s `onOpenChange`. */
  readonly setOpen: (open: boolean) => void;
  /**
   * Attach to `ProductBandDisclosureTrigger`. Opening a band in response to a
   * link scrolls to the band's TITLE rather than to the band's box, so the
   * thing a shopper asked for starts at the top of what they can see instead of
   * one padding step above it.
   */
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
};

export const useProductBandDisclosure = ({
  anchor,
  defaultOpen,
}: UseProductBandDisclosureParameters): ProductBandDisclosureState => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const openIfAddressed = () => {
      if (window.location.hash.slice(1) !== anchor) return;

      setIsOpen(true);
      // After the frame that paints the band open: scrolling to a trigger whose
      // panel is still collapsed lands at the position the page had before the
      // content below it existed.
      requestAnimationFrame(() => {
        triggerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };

    openIfAddressed();

    // A second link to the same band fires no navigation, only a hash change.
    window.addEventListener('hashchange', openIfAddressed);

    return () => {
      window.removeEventListener('hashchange', openIfAddressed);
    };
  }, [anchor]);

  return { isOpen, setOpen: setIsOpen, triggerRef };
};
